import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { LinkWithUTM as Link } from "./LinkWithUTM";
import logoSecondary from "../images/logos/logo_secondary.svg";
import HelpfulFirstStep from "./HelpfulFirstStep";

const FooterLinkGroup = ({ title, links }) => (
	<div className="site-footer__group">
		<h3>{title}</h3>
		<ul>
			{links.map(([label, href, external]) => (
				<li key={`${label}-${href}`}>
					{external ? (
						<a href={href} target="_blank" rel="noopener noreferrer">
							{label}
						</a>
					) : (
						<Link to={href}>{label}</Link>
					)}
				</li>
			))}
		</ul>
	</div>
);

export default function Footer({
	includeCTA = true,
	includeBecomeACarer = false,
}) {
	const {
		site: {
			siteMetadata: {
				appConfig: { domain: appDomain },
			},
		},
	} = useStaticQuery(graphql`
		query RedesignedFooterQuery {
			site {
				siteMetadata {
					appConfig {
						domain
					}
				}
			}
		}
	`);

	return (
		<footer id="site-footer" className="site-footer">
			{includeCTA && <HelpfulFirstStep dark />}

			<div className="site-shell site-footer__main">
				<div className="site-footer__brand">
					<Link to="/" aria-label="Edyn Care home">
						<img src={logoSecondary} alt="Edyn Care" />
					</Link>
					<p>Exceptional live-in care, built around the person.</p>
					<a href="mailto:hello@edyn.care">hello@edyn.care</a>
					<a href="tel:02039709900">020 3970 9900</a>
					{includeBecomeACarer && (
						<Link className="site-footer__carer-link" to="/become-a-carer/">
							Explore carer opportunities
						</Link>
					)}
				</div>

				<div className="site-footer__links">
					<FooterLinkGroup
						title="Care"
						links={[
							["Live-in care", "/liveincare/"],
							["Companionship care", "/companionship-care/"],
							["Couples care", "/couples-care/"],
							["How Edyn works", "/approach/"],
							["Pricing", "/pricing/"],
							["Our carers", "/our-carers/"],
							["Care consultation", "/care-consultation/"],
							["Request a brochure", "/request-a-brochure/"],
						]}
					/>
					<FooterLinkGroup
						title="Conditions"
						links={[
							["Dementia", "/condition/dementia/"],
							["Parkinson's", "/condition/parkinsons/"],
							["Stroke", "/condition/stroke/"],
							["Cancer", "/condition/cancer/"],
							["Multiple sclerosis", "/condition/multiple-sclerosis/"],
						]}
					/>
					<FooterLinkGroup
						title="Edyn"
						links={[
							["About and team", "/our-team/"],
							["Advice", "/blog/"],
							["Become a carer", "/become-a-carer/"],
							["Partner with us", "/partner-with-us/"],
							["Client login", `${appDomain}/login-redirect`, true],
							[
								"CQC report",
								"https://www.cqc.org.uk/location/1-5584086807",
								true,
							],
						]}
					/>
					<FooterLinkGroup
						title="Locations"
						links={[
							["London", "/location/london/"],
							["South East", "/location/south-east/"],
							["South West", "/location/south-west/"],
							["East Midlands", "/location/east-midlands/"],
							["West Midlands", "/location/west-midlands/"],
							["West of England", "/location/west-of-england/"],
						]}
					/>
				</div>
			</div>

			<div className="site-shell site-footer__legal">
				<p>
					Age Abode Limited trading as Edyn Care. Registered in England and
					Wales.
				</p>
				<nav aria-label="Legal navigation">
					<Link to="/privacy/">Privacy</Link>
					<Link to="/cookies/">Cookies</Link>
					<Link to="/terms-and-conditions/">Terms</Link>
				</nav>
				<p>&copy; {new Date().getFullYear()} Edyn Care</p>
			</div>
		</footer>
	);
}
